import { AddressGeoFields } from "@/components/geo/AddressGeoFields";
import { PartyEntityKindField } from "@/components/parties/PartyEntityKindField";
import { PartyFieldLabel, PartyFormSection } from "@/components/parties/PartyFormSection";
import { PartyTypeField } from "@/components/parties/PartyTypeField";
import { Input } from "@/components/ui/input";
import type { CustomerDraft } from "@/lib/customers/customersStore";
import { getStoredAuthUser } from "@/lib/authUser";
import { formatWithMask } from "@/lib/utils";

type Props = {
  draft: CustomerDraft;
  onChange: (draft: CustomerDraft) => void;
  /** Sale quick-create: name/father, cnic, address, phone only. */
  mode?: "full" | "quick";
  disabled?: boolean;
};

export function CustomerFormFields({ draft, onChange, mode = "full", disabled }: Props) {
  const user = getStoredAuthUser();
  const mask = user?.company_branding?.cnic_masking;
  const placeholder = mask ? mask.replace(/9/g, "x") : "xxxxx-xxxxxxx-x";
  const maxLength = mask ? mask.length : 32;
  const isBusiness = draft.entityKind === "business";

  const update = <K extends keyof CustomerDraft>(key: K, value: CustomerDraft[K]) => {
    onChange({ ...draft, [key]: value });
  };

  return (
    <div className="space-y-4">
      <PartyFormSection title="Basic Information">
        <PartyEntityKindField
          role="customer"
          value={draft.entityKind}
          onChange={(entityKind) => update("entityKind", entityKind)}
          disabled={disabled}
        />

        <div className="space-y-2">
          <PartyFieldLabel required>{isBusiness ? "Business Name" : "Customer Name"}</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            value={draft.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={isBusiness ? "e.g. Ali Motors" : "e.g. Ahmed Khan"}
            disabled={disabled}
          />
        </div>

        {isBusiness ? (
          <div className="space-y-2">
            <PartyFieldLabel>NTN / Registration No.</PartyFieldLabel>
            <Input
              className="glass-input h-10"
              value={draft.ntn}
              onChange={(e) => update("ntn", e.target.value)}
              placeholder="e.g. 1234567-8"
              disabled={disabled}
            />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <PartyFieldLabel>Father's Name</PartyFieldLabel>
              <Input
                className="glass-input h-10"
                value={draft.fatherName}
                onChange={(e) => update("fatherName", e.target.value)}
                placeholder="e.g. Muhammad Khan"
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <PartyFieldLabel>CNIC #</PartyFieldLabel>
              <Input
                className="glass-input h-10"
                value={draft.cnic}
                onChange={(e) => update("cnic", mask ? formatWithMask(e.target.value, mask) : e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
              />
            </div>
          </>
        )}

        {mode === "full" ? (
          <PartyTypeField
            role="customer"
            value={draft.type}
            onChange={(type) => update("type", type)}
            disabled={disabled}
          />
        ) : null}
      </PartyFormSection>

      <PartyFormSection title="Contact Information">
        <div className="space-y-2">
          <PartyFieldLabel required>Contact Number</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            type="tel"
            value={draft.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+92 300 1234567"
            disabled={disabled}
          />
        </div>

        {isBusiness ? (
          <div className="space-y-2">
            <PartyFieldLabel>Contact Person</PartyFieldLabel>
            <Input
              className="glass-input h-10"
              value={draft.contactPerson}
              onChange={(e) => update("contactPerson", e.target.value)}
              placeholder="e.g. Ali Hassan"
              disabled={disabled}
            />
          </div>
        ) : null}

        {mode === "full" ? (
          <div className="space-y-2">
            <PartyFieldLabel>Email</PartyFieldLabel>
            <Input
              className="glass-input h-10"
              type="email"
              value={draft.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="email@example.com"
              disabled={disabled}
            />
          </div>
        ) : null}
      </PartyFormSection>

      <PartyFormSection title="Location & Address">
        <AddressGeoFields
          country={draft.country}
          state={draft.state}
          onChange={({ country, state }) => onChange({ ...draft, country, state })}
          disabled={disabled}
        />

        <div className="space-y-2">
          <PartyFieldLabel required>City</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            value={draft.city}
            onChange={(e) => update("city", e.target.value)}
            placeholder="e.g. Lahore"
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <PartyFieldLabel>Postal Code</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            value={draft.postalCode}
            onChange={(e) => update("postalCode", e.target.value)}
            placeholder="e.g. 54000"
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <PartyFieldLabel>Address Line 1</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            value={draft.addressLine1}
            onChange={(e) => update("addressLine1", e.target.value)}
            placeholder="House / street / area"
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <PartyFieldLabel>Address Line 2</PartyFieldLabel>
          <Input
            className="glass-input h-10"
            value={draft.addressLine2}
            onChange={(e) => update("addressLine2", e.target.value)}
            placeholder="Apartment, suite, landmark (optional)"
            disabled={disabled}
          />
        </div>
      </PartyFormSection>
    </div>
  );
}
